import {alpha, createTheme} from "@mui/material/styles"
import type {} from '@mui/lab/themeAugmentation';

const headerFontFamily = "'Rubik', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Oxygen',\n" +
    "    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',\n" +
    "    sans-serif";

export const whiteInputsTheme = createTheme({
    palette: {
        primary: {
            main: '#fff',
        },
        secondary: {
            main: '#070607',
        },
        tertiary: {
            light: '#FFE34D',
            main: '#FFD700',
            dark: '#CCAC00',
        },
        quaternary: {
            light: '#7C29C0',
            main: '#541C82',
            dark: '#2B0E43',
        }
    },
    typography: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Oxygen',\n" +
            "    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',\n" +
            "    sans-serif",
        h1: {
            fontFamily: headerFontFamily,
        },
        h2: {
            fontFamily: headerFontFamily,
        },
        h3: {
            fontFamily: headerFontFamily,
        },
        h4: {
            fontFamily: headerFontFamily,
        },
        h5: {
            fontFamily: headerFontFamily,
        },
        h6: {
            fontFamily: headerFontFamily,
        }
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: `
        input:-webkit-autofill {
          -webkit-text-fill-color: white;
        }
      `,
        },
        MuiListItemButton: {
            styleOverrides: {
                root: ({theme}) => ({
                    backdropFilter: "blur(5px)",

                    '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },

                    '&.Mui-focusVisible, &:focus': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.06),
                        outline: 'none',
                    },

                    '&:active': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    },

                    '&.Mui-selected': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.15),
                        '&:hover, &.Mui-focusVisible, &:focus': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.17),
                        },
                        '&:active': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.21),
                        },
                    },
                }),
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                containedPrimary: ({theme}) => ({
                    "&:disabled": {
                        color: alpha(theme.palette.primary.main, .2),
                        backgroundColor: alpha(theme.palette.primary.main, .12),
                    },
                }),
                root: {
                    textTransform: 'initial',
                    borderRadius: 20,
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: ({theme}) => ({
                    color: theme.palette.primary.main,
                }),
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: ({theme}) => ({
                    "&:disabled": {
                        color: alpha(theme.palette.primary.main, .5),
                    },
                }),
            },
        },
        MuiSkeleton: {
            styleOverrides: {
                root: ({theme}) => ({
                    background: alpha(theme.palette.primary.main, .5),
                }),
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: ({theme}) => ({
                    color: alpha(theme.palette.primary.main, .8),
                    "& > svg": {
                        color: alpha(theme.palette.primary.main, .8),
                    },
                    "&:hover": {
                        "&:not(.Mui-error) > fieldset": {
                            borderColor: theme.palette.primary.main, // Fully opaque
                        },
                    },
                    "&.Mui-error": {
                        "& input": {
                            color: theme.palette.error.main,
                        },
                    },
                }),
                notchedOutline: ({theme}) => ({
                    borderColor: alpha(theme.palette.primary.main, .8),
                }),
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: ({theme}) => ({
                    "&:not(.Mui-error)": {
                        color: alpha(theme.palette.primary.main, .8),
                    },
                }),
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: ({theme}) => ({
                    "&:not(.Mui-error)": {
                        color: alpha(theme.palette.primary.main, .8),
                    },
                }),
            },
        },
        MuiTab: {
            styleOverrides: {
                textColorPrimary: ({theme}) => ({
                    "&:not(.Mui-selected)": {
                        color: alpha(theme.palette.primary.main, .5),
                    },
                }),
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: ({theme}) => ({
                    backgroundColor: alpha(theme.palette.secondary.main, .8),
                    backdropFilter: "blur(5px)",
                    border: `1px solid ${theme.palette.primary.main}`,
                }),
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    minWidth: '10em',
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    color: "white",
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: ({theme}) => ({
                    backgroundColor: alpha(theme.palette.primary.main, .15),
                }),
            },
        },
        MuiTableBody: {
            styleOverrides: {
                root: ({theme}) => ({
                    backgroundColor: alpha(theme.palette.primary.main, .05),
                }),
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    color: "white",
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: ({theme}) => ({
                    '&.Mui-selected': {
                        backgroundColor: `${theme.palette.action.hover} !important`,
                        color: theme.palette.quaternary.main,
                    },
                }),
            },
        },
        MuiStep: {
            styleOverrides: {
                root: {
                    paddingLeft: '0.2em',
                    paddingRight: '0.2em',
                },
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                label: ({theme}) => ({
                    marginTop: '0.5em !important',
                    color: alpha(theme.palette.primary.main, .2),
                    fontSize: '0.75rem',
                    '&.Mui-active': {
                        color: theme.palette.primary.main,
                    },
                    '&.Mui-completed': {
                        color: theme.palette.primary.main,
                    },
                }),
            },
        },
        MuiStepIcon: {
            styleOverrides: {
                root: ({theme}) => ({
                    color: alpha(theme.palette.primary.main, .2),
                    '&.Mui-active': {
                        color: theme.palette.primary.main,
                    },
                    '&.Mui-completed': {
                        color: theme.palette.primary.main,
                    },
                }),
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: ({theme}) => ({
                    color: alpha(theme.palette.primary.main, .8),
                }),
            },
        },
        MuiLoadingButton: {
            styleOverrides: {
                loadingIndicator: ({theme}) => ({
                    color: alpha(theme.palette.primary.main, .8),
                }),
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: ({theme}) => ({
                    "&.Mui-disabled": {
                        color: alpha(theme.palette.primary.main, 0.4),
                    },
                }),
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: ({theme}) => ({
                    "&.Mui-disabled.MuiRadio-colorPrimary": {
                        color: alpha(theme.palette.primary.main, 0.3),
                    },
                }),
            },
        },
    },
});
